import { createFileRoute, Link, Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import { useEffect } from "react";
import { Loader2, LogOut, LayoutList, Car, Settings as SettingsIcon } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";

export const Route = createFileRoute("/partner")({
  head: () => ({
    meta: [
      { title: "Partner Backoffice — Bacha Way Travel" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: PartnerLayout,
});

function PartnerLayout() {
  const { user, isAuthenticated, loading, signOut } = useAuth();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  useEffect(() => {
    if (!loading && !isAuthenticated) navigate({ to: "/login", replace: true });
  }, [loading, isAuthenticated, navigate]);

  if (loading || !isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  const nav: Array<{ to: string; label: string; icon: typeof LayoutList; exact?: boolean }> = [
    { to: "/partner", label: "Orders", icon: LayoutList, exact: true },
    { to: "/partner/fleet", label: "Fleet", icon: Car },
    { to: "/partner/settings", label: "Settings", icon: SettingsIcon },
  ];

  const isActive = (to: string, exact?: boolean) =>
    exact ? pathname === to : pathname === to || pathname.startsWith(to + "/");

  return (
    <div className="min-h-screen bg-muted/20 flex">
      <aside className="hidden md:flex md:w-64 flex-col border-r bg-card">
        <div className="px-6 py-6 border-b">
          <h1 className="text-lg font-serif text-primary">Bacha Way</h1>
          <p className="text-xs text-muted-foreground mt-0.5">Partner Backoffice</p>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {nav.map(({ to, label, icon: Icon, exact }) => {
            const active = isActive(to, exact);
            return (
              <Link
                key={to}
                to={to}
                className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                  active
                    ? "bg-primary text-primary-foreground"
                    : "text-foreground hover:bg-muted"
                }`}
              >
                <Icon className="h-4 w-4" />
                {label}
              </Link>
            );
          })}
        </nav>
        <div className="p-3 border-t">
          <div className="px-3 py-2 text-xs text-muted-foreground truncate">{user?.email}</div>
          <button
            onClick={() => signOut().then(() => navigate({ to: "/" }))}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="md:hidden border-b bg-card px-4 py-3 flex items-center justify-between">
          <h1 className="text-base font-serif text-primary">Partner Backoffice</h1>
          <button
            onClick={() => signOut().then(() => navigate({ to: "/" }))}
            className="text-xs text-muted-foreground"
          >
            Sign out
          </button>
        </header>
        <nav className="md:hidden flex gap-1 px-2 py-2 bg-card border-b overflow-x-auto">
          {nav.map(({ to, label, icon: Icon, exact }) => {
            const active = isActive(to, exact);
            return (
              <Link
                key={to}
                to={to}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs whitespace-nowrap ${
                  active ? "bg-primary text-primary-foreground" : "text-muted-foreground"
                }`}
              >
                <Icon className="h-3.5 w-3.5" />
                {label}
              </Link>
            );
          })}
        </nav>
        <main className="flex-1 p-4 md:p-8 overflow-x-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
